import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { id: "color-pickr-component" }

import { onMounted, onUnmounted, ref, computed, markRaw } from "vue";
import { fabric } from "fabric";
import Pickr from "@simonwep/pickr";
import { store } from "@/store";
import { undoActionStack } from "@/canvas-api/ActionStack";
import { updateText } from "@/canvas-api/UserObjects/Text";
import { updateClipArt } from "@/canvas-api/UserObjects/ClipArt";
import { updateShape } from "@/canvas-api/UserObjects/SvgShape";
import { updateImage } from "@/canvas-api/UserObjects/Image";

import { ColorPickrState } from "@/types";
import { ActionTypes } from "@/store/actions";


export default /*#__PURE__*/_defineComponent({
  emits: ["setPickrIsRendered"],
  setup(__props, { emit }: { emit: ({
  (e: "setPickrIsRendered", value: boolean): void;
}), expose: any, slots: any, attrs: any }) {

// Vue 3.2 script setup api


const pickr = ref({} as Pickr);
const container = ref<HTMLDivElement>();

const isMobile = computed<boolean>(() => store.getters.isMobile);
const currentLabelBgColor = computed<string>(() => store.getters.currentLabelBgColor);
const currentActiveObject = computed<fabric.Object | undefined>(
  () => store.getters.currentActiveObject
);
const colorPickrState = computed<ColorPickrState>(() => store.getters.colorPickrState);

onMounted(() => {
  const newElement = document.createElement("div");

  if (container.value) {
    container.value.appendChild(newElement);

    pickr.value = markRaw(
      Pickr.create({
        el: newElement,
        theme: "classic",
        appClass: "pickr-main",
        useAsButton: true,
        showAlways: true,
        autoReposition: false,
        default: colorPickrState.value.property.value
          ? (colorPickrState.value.property.value as string)
          : "#000",
        swatches: [
          "rgba(244, 67, 54, 1)",
          "rgba(233, 30, 99, 1)",
          "rgba(156, 39, 176, 1)",
          "rgba(103, 58, 183, 1)",
          "rgba(63, 81, 181, 1)",
          "rgba(33, 150, 243, 1)",
          "rgba(3, 169, 244, 1)",
          "rgba(0, 188, 212, 1)",
          "rgba(0, 150, 136, 1)",
          "rgba(76, 175, 80, 1)",
        ],
        components: {
          // Main components
          preview: true,
          opacity: true,
          hue: true,

          // Input / output Options
          interaction: {
            hex: true,
            rgba: true,
            hsla: false,
            hsva: false,
            cmyk: false,
            input: true,
            clear: true,
            save: true,
          },
        },
      })
    );

    pickr.value.show();
    pickr.value.on("init", initPickr);
    pickr.value.on("save", handleSave);
  }
});

onUnmounted(() => {
  if (Object.entries(pickr.value).length) {
    pickr.value.destroyAndRemove();
  }
});

const dragPickr = (
  wrap: HTMLDivElement,
  palette: HTMLDivElement,
  colorChooser: HTMLDivElement,
  opacityChooser: HTMLDivElement
) => {
  let initX: number | undefined;
  let initY: number | undefined;
  let wrapBCR = wrap.getBoundingClientRect();

  wrap.addEventListener("mousedown", (ev: MouseEvent) => {
    wrap.style.cursor = "grabbing";

    // ev.preventDefault();
    ev.stopPropagation();

    if (ev.target instanceof Node && (ev.target === wrap || wrap.contains(ev.target))) {
      if (
        ev.target !== palette &&
        ev.target !== colorChooser &&
        ev.target !== opacityChooser &&
        !palette.contains(ev.target) &&
        !colorChooser.contains(ev.target) &&
        !opacityChooser.contains(ev.target)
      ) {
        initX = ev.x;
        initY = ev.y;
      }
    }
  });

  document.addEventListener("mousemove", (ev: MouseEvent) => {
    // ev.preventDefault();
    ev.stopPropagation();

    if (initX && initY) {
      wrap.style.left = `${wrapBCR.left + (ev.x - initX)}px`;
      wrap.style.top = `${wrapBCR.top + (ev.y - initY)}px`;
    }
  });

  document.addEventListener("mouseup", (ev: MouseEvent) => {
    ev.preventDefault();
    ev.stopPropagation();

    wrapBCR = wrap.getBoundingClientRect();
    wrap.style.cursor = "grab";
    initX = undefined;
    initY = undefined;
  });
};

const initPickr = () => {
  const pickrWrap = document.querySelector(".pcr-app") as HTMLDivElement;
  const pickrSelection = document.querySelector(".pcr-selection") as HTMLDivElement;
  const pickrColorPalette = document.querySelector(".pcr-color-palette") as HTMLDivElement;
  const pickrColorChooser = document.querySelector(".pcr-color-chooser") as HTMLDivElement;
  const pickrColorOpacity = document.querySelector(".pcr-color-opacity") as HTMLDivElement;
  const pickrSwatches = document.querySelector(".pcr-swatches") as HTMLDivElement;
  const pickrInteraction = document.querySelector(".pcr-interaction") as HTMLDivElement;
  const pickrSave = document.querySelector(".pcr-save") as HTMLInputElement;

  pickrSave.value = "Apply";

  const pickrPaletteContainer = document.createElement("div");
  const pickrTypesContainer = document.createElement("div");

  pickrPaletteContainer.className = "pcr-palette-swatches-wrap";
  pickrTypesContainer.className = "pcr-types-wrap";

  const pickrTypes = document.getElementsByClassName("pcr-type");
  const pickrTypesActive = document.getElementsByClassName("pcr-type");

  for (const el of pickrTypes) {
    pickrTypesContainer.append(el);
  }

  for (const el of pickrTypesActive) {
    pickrTypesContainer.append(el);
  }

  pickrInteraction.insertBefore(pickrTypesContainer, pickrSave);
  pickrSelection.insertBefore(pickrPaletteContainer, pickrColorPalette);
  pickrPaletteContainer.append(pickrColorPalette, pickrSwatches);

  if (pickrWrap) {
    pickrWrap.style.left = isMobile.value ? "0px" : "75px";
    pickrWrap.style.top = "120px";
    pickrWrap.style.cursor = "grab";

    if (colorPickrState.value.left) {
      pickrWrap.style.left = colorPickrState.value.left;
    }

    if (colorPickrState.value.top) {
      pickrWrap.style.top = colorPickrState.value.top;
    }

    dragPickr(pickrWrap, pickrColorPalette, pickrColorChooser, pickrColorOpacity);
    emit("setPickrIsRendered", true);
  } else {
    console.log("Error when initializing color-pickr");
  }
};

const handleSave = (color: Pickr.HSVaColor) => {
  if (color) {
    const rBGAString: string = color.toRGBA().toString();

    const id: string = colorPickrState.value.id;
    const target: HTMLElement | null = document.querySelector(`#${id}`);
    if (target) target.style.background = `${rBGAString}`;

    const pickrMain = document.querySelector(".pickr-main") as HTMLDivElement;
    if (pickrMain) {
      if (id === "canvas-bg-color") {
        undoActionStack.push({
          id: 0,
          type: "canvasBgColor",
          canvasBgColor: currentLabelBgColor.value,
        });

        const payload = {
          left: pickrMain.style.left,
          top: pickrMain.style.top,
          isOpen: false,
          id: "",
          property: { name: "", value: rBGAString },
        };

        dispatchPickrState(payload)
        store.dispatch(ActionTypes.SET_LABEL_BG_COLOR, rBGAString);
      } else {
        if (currentActiveObject.value) {
          switch (currentActiveObject.value.name) {
            case "iText": {
              updateText(colorPickrState.value.property.name, rBGAString);
              break;
            }
            case "svgShape": {
              updateShape(colorPickrState.value.property.name, rBGAString);
              break;
            }
            case "clipArt": {
              updateClipArt(colorPickrState.value.property.name, rBGAString);
              break;
            }
            case "image": {
              updateImage(colorPickrState.value.property.name, rBGAString);
              break;
            }
          }

          const payload = {
            left: pickrMain.style.left,
            top: pickrMain.style.top,
            isOpen: false,
            id: "",
            property: { name: "", value: rBGAString },
          };

          dispatchPickrState(payload);
        }
      }

      emit("setPickrIsRendered", false);
    } else {
      dispatchPickrState();
      emit("setPickrIsRendered", false);
    }
  } else {
    dispatchPickrState();
    emit("setPickrIsRendered", false);
  }
};

const dispatchPickrState = (payload?: ColorPickrState) => {
  if (payload) {
    store.dispatch(ActionTypes.SET_COLOR_PICKR_STATE, payload);
  } else {
    store.dispatch(ActionTypes.SET_COLOR_PICKR_STATE, {
      ...colorPickrState.value,
      isOpen: false,
      id: "",
      property: { name: "", value: "" },
    });
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      id: "color-picker-container",
      ref: (_value, _refs) => {
        _refs['container'] = _value
        container.value = _value
      }
    }, null, 512)
  ]))
}
}

})